import { useQueries, useQuery } from '@tanstack/react-query'
import { fetchApiWithToken, getBatchFetcher } from '~/api/utils'
import type { ApplicantHeadData } from '../types'

const applicantHeadBatcher = <T extends ApplicantHeadData>(
  additionalFields: string[]
) =>
  getBatchFetcher<T>(`applicantHead_${additionalFields.join('_')}`, (ids) =>
    fetchApiWithToken<T[]>({
      method: 'get',
      url: `/v1/applicants?id[]=${ids.join('&id[]=')}&itemsPerPage=1000&isArchived=all&fields[]=id&fields[]=firstname&fields[]=lastname&fields[]=acceptsMessaging${additionalFields ? `&fields[]=${additionalFields.join('&fields[]=')}` : ''}`
    })
  )

export const useApplicantsHead = <T extends ApplicantHeadData>({
  applicantIds,
  additionalFields
}: {
  applicantIds: number[]
  additionalFields?: string[]
}) => {
  return useQueries({
    queries: applicantIds.map((applicantId) => ({
      queryKey: ['applicants', 'head', applicantId, additionalFields],
      queryFn: () =>
        applicantHeadBatcher<T>(additionalFields ?? []).fetch(applicantId),
      staleTime: 300_000
    }))
  })
}

export const useApplicantHead = <T extends ApplicantHeadData>({
  applicantId,
  additionalFields
}: {
  applicantId?: number
  additionalFields?: string[]
}) => {
  return useQuery({
    queryKey: ['applicants', 'head', applicantId, additionalFields],
    queryFn: () =>
      applicantHeadBatcher<T>(additionalFields ?? []).fetch(
        applicantId as number
      ),
    enabled: !!applicantId,
    staleTime: 300_000
  })
}
